// src/components/lead/sections/DuplicateLeadModal.tsx
import type { Lead } from "@/types/lead";
import { cardMutedTextClass, cardSurfaceClass, cardTitleTextClass, ghostBtnClass } from "@/components/common/surfaceClasses";

export function DuplicateLeadModal(props: {
  pendingSendLead: Lead | null;
  duplicateMatches: Lead[];
  duplicateReason: string | null;

  canSpeakHelp: boolean;
  speak: (t: string) => void;

  sendToGHL: (lead: Lead) => void;

  setPendingSendLead: (l: Lead | null) => void;
  setDuplicateMatches: (l: Lead[]) => void;
  setDuplicateReason: (r: string | null) => void;

  setCurrentLead: (l: Lead | null) => void;
  currentLeadRef: React.MutableRefObject<Lead | null>;

  setTranscript: (t: string) => void;
  transcriptRef: React.MutableRefObject<string>;

  setIsListening: (v: boolean) => void;
  listeningRef: React.MutableRefObject<boolean>;

  setMergeSuggestion: (v: any) => void;
  findMergeSuggestionForCurrent: () => any;
}) {
  const {
    pendingSendLead,
    duplicateMatches,
    duplicateReason,

    canSpeakHelp,
    speak,

    sendToGHL,

    setPendingSendLead,
    setDuplicateMatches,
    setDuplicateReason,

    setCurrentLead,
    currentLeadRef,

    setTranscript,
    transcriptRef,

    setIsListening,
    listeningRef,

    setMergeSuggestion,
    findMergeSuggestionForCurrent,
  } = props;

  if (!pendingSendLead || duplicateMatches.length === 0) return null;

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40">
      <div className={`rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6 text-left ${cardSurfaceClass}`}>
        <h2 className={`text-xl font-bold mb-2 ${cardTitleTextClass}`}>Possible duplicate lead detected</h2>

        {duplicateReason ? (
          <p className="text-sm text-gray-700 mb-2">
            <span className="font-semibold">Reason:</span> {duplicateReason}
          </p>
        ) : (
          <p className="text-sm text-gray-600 mb-2">
            This lead looks similar to an existing lead.
          </p>
        )}

        <p className="text-sm text-gray-600 mb-3">What would you like to do?</p>

        <div className={`mb-3 max-h-40 overflow-y-auto border rounded-xl p-2 text-xs dark:border-zinc-700 ${cardMutedTextClass}`}>
          {duplicateMatches.map((l) => {
            const le = l.extracted;
            const displayName =
              le?.first_name || le?.last_name
                ? `${le?.first_name ?? ""} ${le?.last_name ?? ""}`.trim()
                : l.name || "Unnamed lead";

            const when = new Date(l.lastUpdated).toLocaleTimeString([], {
              hour: "numeric",
              minute: "2-digit",
            });

            return (
              <div key={l.id} className="py-1 border-b last:border-b-0 border-gray-100">
                <div className="font-semibold">{displayName}</div>
                <div className="text-gray-500">
                  {le?.phone && <span>Phone: {le.phone} • </span>}
                  {le?.email && <span>Email: {le.email} • </span>}
                  <span>Updated: {when}</span>
                </div>
              </div>
            );
          })}
        </div>

        <div className="space-y-2">
          <button
            type="button"
            className="w-full py-2.5 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700"
            onClick={() => {
              if (canSpeakHelp) speak("Sending as a separate lead.");
              sendToGHL(pendingSendLead);

              setPendingSendLead(null);
              setDuplicateMatches([]);
              setDuplicateReason(null);
            }}
          >
            Send anyway as a new lead
          </button>

          <button
            type="button"
            className="w-full py-2.5 rounded-xl bg-gray-100 text-gray-800 text-sm font-semibold hover:bg-gray-200"
            onClick={() => {
              const first = duplicateMatches[0];
              if (first) {
                setCurrentLead(first);
                currentLeadRef.current = first;

                setTranscript(first.transcript);
                transcriptRef.current = first.transcript;

                setIsListening(false);
                listeningRef.current = false;

                setMergeSuggestion(null);
                setTimeout(() => setMergeSuggestion(findMergeSuggestionForCurrent()), 0);

                if (canSpeakHelp) speak("Loaded the existing lead so you can review it.");
              }

              setPendingSendLead(null);
              setDuplicateMatches([]);
              setDuplicateReason(null);
            }}
          >
            Open existing lead instead
          </button>

          <button
            type="button"
            className={`w-full py-2.5 rounded-xl text-sm font-semibold ${ghostBtnClass}`}
            onClick={() => {
              setPendingSendLead(null);
              setDuplicateMatches([]);
              setDuplicateReason(null);
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}